import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Alert,
  AlertDescription,
} from './ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Upload, 
  FileText, 
  Image, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Sparkles,
  X,
  User,
  Calendar,
  ClipboardList,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';
import { TASK_TYPE_LABELS, PRIORITY_LABELS } from '../lib/utils';

const ImportarTarefasIA = ({ onImportComplete }) => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({});

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não suportado. Use PDF, DOCX, JPG, PNG ou WEBP.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 10MB.');
      return;
    }

    setSelectedFile(file);
    setExtractedData(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/import/extract-tasks-from-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setExtractedData(response.data);
      
      if (response.data.tarefas?.length === 0) {
        toast.warning('Nenhuma tarefa encontrada no documento');
      } else {
        toast.success(`${response.data.tarefas?.length} tarefa(s) encontrada(s)`);
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || 'Erro ao processar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmAll = async () => {
    if (!extractedData?.tarefas?.length) return;

    // Filter only tasks with matched patients
    const tarefasValidas = extractedData.tarefas.filter(t => t.paciente_id);
    
    if (tarefasValidas.length === 0) {
      toast.error('Nenhuma tarefa com paciente válido para criar');
      return;
    }

    setConfirming(true);
    try {
      const response = await api.post('/import/confirm-tasks', tarefasValidas);
      
      if (response.data.criadas > 0) {
        toast.success(response.data.mensagem);
        setModalOpen(false);
        setExtractedData(null);
        setSelectedFile(null);
        onImportComplete?.(response.data.tarefas_criadas);
      }
      
      if (response.data.erros > 0) {
        response.data.detalhes_erros.forEach(err => {
          toast.error(`Tarefa ${err.indice + 1}: ${err.erro}`);
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar importação');
    } finally {
      setConfirming(false);
    }
  };

  const handleEditTask = (index) => {
    setEditingIndex(index);
    setEditForm({ ...extractedData.tarefas[index] });
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null) {
      const newTarefas = [...extractedData.tarefas];
      newTarefas[editingIndex] = editForm;
      setExtractedData({ ...extractedData, tarefas: newTarefas });
      setEditingIndex(null);
      setEditForm({});
    }
  };

  const handleRemoveTask = (index) => {
    const newTarefas = extractedData.tarefas.filter((_, i) => i !== index);
    setExtractedData({ ...extractedData, tarefas: newTarefas });
  };

  const getConfidenceColor = (confianca) => {
    if (confianca >= 0.8) return 'text-green-600 bg-green-50';
    if (confianca >= 0.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getFileIcon = () => {
    if (!selectedFile) return <Upload className="h-8 w-8" />;
    if (selectedFile.type.includes('image')) return <Image className="h-8 w-8" />;
    return <FileText className="h-8 w-8" />;
  };

  const tarefasComPaciente = extractedData?.tarefas?.filter(t => t.paciente_id) || [];

  return (
    <>
      <Button
        variant="outline"
        className="gap-2"
        onClick={() => setModalOpen(true)}
        data-testid="btn-importar-tarefas-ia"
      >
        <Sparkles className="h-4 w-4" />
        Importar com IA
      </Button>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Importar Tarefas com IA
            </DialogTitle>
            <DialogDescription>
              Faça upload de um documento (PDF, DOCX) ou foto para extrair tarefas automaticamente
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {/* File Upload Area */}
            {!extractedData && (
              <div className="space-y-4">
                <div
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`
                    border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
                    transition-colors duration-200
                    ${uploading ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'}
                  `}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {uploading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="h-10 w-10 animate-spin text-primary" />
                      <p className="text-sm font-medium">Processando documento com IA...</p>
                      <p className="text-xs text-muted-foreground">Isso pode levar alguns segundos</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      {getFileIcon()}
                      <div>
                        <p className="font-medium">Clique para selecionar ou arraste um arquivo</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          PDF, DOCX, JPG, PNG ou WEBP (máx. 10MB)
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {selectedFile && !uploading && (
                  <Alert>
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      Arquivo selecionado: <strong>{selectedFile.name}</strong>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Extracted Data Review */}
            {extractedData && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="font-medium">
                      {extractedData.tarefas?.length || 0} tarefa(s) encontrada(s)
                    </span>
                    {tarefasComPaciente.length < extractedData.tarefas?.length && (
                      <Badge variant="outline" className="text-yellow-600">
                        {tarefasComPaciente.length} com paciente
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setExtractedData(null);
                      setSelectedFile(null);
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                </div>

                {/* Task List */}
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {extractedData.tarefas?.map((tarefa, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 space-y-3 bg-card ${!tarefa.paciente_id ? 'opacity-60' : ''}`}
                      >
                        {editingIndex === index ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs">Título</Label>
                                <Input
                                  value={editForm.titulo || ''}
                                  onChange={(e) => setEditForm({ ...editForm, titulo: e.target.value })}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Tipo</Label>
                                <Select
                                  value={editForm.tipo || 'visita'}
                                  onValueChange={(v) => setEditForm({ ...editForm, tipo: v })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                                      <SelectItem key={value} value={value}>{label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Prioridade</Label>
                                <Select
                                  value={editForm.prioridade || 'media'}
                                  onValueChange={(v) => setEditForm({ ...editForm, prioridade: v })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                                      <SelectItem key={value} value={value}>{label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Data Prevista</Label>
                                <Input
                                  type="date"
                                  value={editForm.data_prevista || ''}
                                  onChange={(e) => setEditForm({ ...editForm, data_prevista: e.target.value })}
                                />
                              </div>
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs">Observações</Label>
                                <Input
                                  value={editForm.observacoes || ''}
                                  onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })}
                                />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex(null)}
                              >
                                Cancelar
                              </Button>
                              <Button size="sm" onClick={handleSaveEdit}>
                                Salvar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <>
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2 flex-1">
                                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                                <span className="font-semibold flex-1">{tarefa.titulo || 'Tarefa sem título'}</span>
                                <Badge className={`text-xs ${getConfidenceColor(tarefa.confianca || 0)}`}>
                                  {Math.round((tarefa.confianca || 0) * 100)}%
                                </Badge>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleEditTask(index)}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => handleRemoveTask(index)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="flex items-center gap-1">
                                <Badge variant="outline">
                                  {TASK_TYPE_LABELS[tarefa.tipo] || tarefa.tipo}
                                </Badge>
                                <Badge variant="outline">
                                  {PRIORITY_LABELS[tarefa.prioridade] || tarefa.prioridade}
                                </Badge>
                              </div>
                              {tarefa.data_prevista && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Calendar className="h-3 w-3" /> {tarefa.data_prevista}
                                </div>
                              )}
                              {tarefa.paciente_nome_sistema ? (
                                <div className="flex items-center gap-1 text-green-600 col-span-2">
                                  <User className="h-3 w-3" /> 
                                  <CheckCircle2 className="h-3 w-3" />
                                  {tarefa.paciente_nome_sistema}
                                </div>
                              ) : tarefa.paciente_nome ? (
                                <div className="flex items-center gap-1 text-yellow-600 col-span-2">
                                  <User className="h-3 w-3" />
                                  <AlertCircle className="h-3 w-3" />
                                  {tarefa.paciente_nome} (não encontrado no sistema)
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-600 col-span-2">
                                  <User className="h-3 w-3" />
                                  <X className="h-3 w-3" />
                                  Paciente não identificado
                                </div>
                              )}
                              {tarefa.observacoes && (
                                <div className="text-muted-foreground col-span-2 text-xs">
                                  {tarefa.observacoes}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {/* Warning for tasks without patient */}
                {tarefasComPaciente.length < extractedData.tarefas?.length && (
                  <Alert variant="warning" className="border-yellow-500 bg-yellow-50">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-700">
                      {extractedData.tarefas.length - tarefasComPaciente.length} tarefa(s) sem paciente válido não serão criadas.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setModalOpen(false);
                setExtractedData(null);
                setSelectedFile(null);
              }}
            >
              Cancelar
            </Button>
            
            {tarefasComPaciente.length > 0 && (
              <Button
                onClick={handleConfirmAll}
                disabled={confirming}
                className="gap-2"
              >
                {confirming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Cadastrar ({tarefasComPaciente.length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportarTarefasIA;
